import { createContext, useContext, useState, useEffect, useCallback } from "react";

// Persists "last opened lead" across pages so the TopClockBar can keep
// showing the lead's local time even after the user navigates away.
// Stored in sessionStorage so it dies with the browser session.

const STORAGE_KEY = "gtx_crm_focused_lead";
const LeadFocusContext = createContext(null);

export function LeadFocusProvider({ children }) {
  const [focusedLead, setFocusedLead] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (focusedLead) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(focusedLead));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // ignore quota errors
    }
  }, [focusedLead]);

  const setLead = useCallback((lead) => {
    if (!lead) {
      setFocusedLead(null);
      return;
    }
    // Keep only the fields TopClockBar actually needs.
    setFocusedLead({
      id: lead.id,
      company_name: lead.company_name,
      country: lead.country,
      timezone: lead.timezone || null,
    });
  }, []);

  const clearLead = useCallback(() => setFocusedLead(null), []);

  return (
    <LeadFocusContext.Provider value={{ focusedLead, setLead, clearLead }}>
      {children}
    </LeadFocusContext.Provider>
  );
}

export function useLeadFocus() {
  const ctx = useContext(LeadFocusContext);
  if (!ctx) return { focusedLead: null, setLead: () => {}, clearLead: () => {} };
  return ctx;
}
